import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ShopBottomNav from '../../components/ShopBottomNav';
import { generateInvoice, generateInvoiceNumber } from '../../services/invoiceService';
import {
  addPurchasedTokens,
  setUnlimited,
  setTokenPlan,
  applyPlanFromBackend,
  setTokenBalance,
} from '../../services/shopState';
import { getTokenPackages, purchasePackage, FALLBACK_PACKAGES, normalizePackage } from '../../services/apiService';
import './ShopCheckoutPage.css';

const formatDate = (d) =>
  d.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });

// `serverOrder` is the order row the backend created (invoice number, id);
// without it (backend < 0.3) a local number is generated as before.
const buildOrderData = (pkg, paymentMethod, serverOrder) => {
  const today = new Date();
  const txId = serverOrder?.id ? String(serverOrder.id).slice(0, 8).toUpperCase() : `TX-${Date.now().toString().slice(-8)}`;
  return {
    invoiceNumber: serverOrder?.invoiceNumber || generateInvoiceNumber(txId),
    date: formatDate(today),
    shopName: sessionStorage.getItem('walkin_shop_name') || 'Mijn winkel',
    email: sessionStorage.getItem('walkin_shop_email') || 'shop@walkin.nl',
    address: 'Voorbeeldstraat 1',
    city: '1011 AA Amsterdam',
    vatNumber: '',
    packageName: pkg.name,
    tokens: pkg.tokensLabel,
    priceEx: pkg.priceEx,
    vat: pkg.vat,
    priceIncl: pkg.priceIncl,
    paymentMethod: paymentMethod || 'iDEAL',
    transactionId: txId,
  };
};

export default function ShopCheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paid, setPaid] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('iDEAL');
  const [showPreview, setShowPreview] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState('idle'); // idle | sending | sent
  const [toast, setToast] = useState('');
  const orderRef = useRef(null);

  const requestedId = searchParams.get('package') || 'unlimited';
  // Packages and prices come from the database (token_packages). Until they
  // have loaded, the 0.2 defaults are shown.
  const [packages, setPackages] = useState(() => FALLBACK_PACKAGES.map(normalizePackage));
  const [packagesLive, setPackagesLive] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState('');
  useEffect(() => {
    let cancelled = false;
    getTokenPackages().then(({ packages: list, live }) => {
      if (cancelled) return;
      setPackages(list);
      setPackagesLive(live);
    });
    return () => { cancelled = true; };
  }, []);
  const pkg = packages.find((p) => p.id === requestedId)
    || packages.find((p) => p.type === 'subscription')
    || packages[0];

  const order = useMemo(() => orderRef.current, [paid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(''), 2800);
    return () => clearTimeout(id);
  }, [toast]);

  const handlePay = async () => {
    if (paying) return;
    setPaying(true);
    setPayError('');

    // Backend 0.3: one call creates the order, adds the tokens or starts
    // Unlimited, and writes the ledger — so the purchase is in the database
    // and visible to the admin straight away.
    let serverOrder = null;
    let usedBackend = false;
    if (packagesLive) {
      try {
        const { order, retailer } = await purchasePackage(pkg.id, paymentMethod);
        serverOrder = order;
        usedBackend = true;
        if (retailer) {
          applyPlanFromBackend(retailer);
          if (Number.isFinite(retailer.tokensAvailable)) {
            setTokenBalance({
              purchased: retailer.tokensPurchased,
              available: retailer.tokensAvailable,
              used: retailer.tokensUsed,
            });
          }
        }
      } catch (err) {
        setPaying(false);
        setPayError(err?.message || 'Betaling kon niet worden verwerkt. Er is niets afgeschreven.');
        return;
      }
    }

    if (!usedBackend) {
      // Backend without token packages yet (< 0.3): the old browser-only flow.
      if (pkg.type === 'subscription') {
        setUnlimited();
      } else if (typeof pkg.tokens === 'number') {
        addPurchasedTokens(pkg.tokens);
        setTokenPlan(pkg.id);
      }
    }

    orderRef.current = buildOrderData(pkg, paymentMethod, serverOrder);
    setPaying(false);
    setPaid(true);
    setEmailStatus('sending');
    setTimeout(() => setEmailStatus('sent'), 1800);
  };

  const handleDownloadPdf = () => {
    if (!orderRef.current) return;
    generateInvoice(orderRef.current);
  };

  const handleEmail = () => {
    const email = orderRef.current?.email || 'shop@walkin.nl';
    setToast(`Factuur verstuurd naar ${email}`);
  };

  return (
    <div className="shop-checkout">
      <header className="shop-checkout__header">
        <button
          className="shop-checkout__back"
          onClick={() => navigate(-1)}
          aria-label="Terug"
        >
          ←
        </button>
        <h1 className="shop-checkout__title">
          {paid ? 'Bevestiging' : 'Afrekenen'}
        </h1>
        <span className="shop-checkout__spacer" />
      </header>

      <main className="shop-checkout__main">
        {!paid ? (
          <>
            <section className="shop-checkout__summary">
              <div className="shop-checkout__summary-head">
                <span className="shop-checkout__summary-label">
                  Gekozen pakket
                </span>
                <span
                  className={`shop-checkout__pill shop-checkout__pill--${pkg.id}`}
                >
                  {pkg.name}
                </span>
              </div>
              <div className="shop-checkout__summary-row">
                <span>Aantal deals</span>
                <strong>{pkg.tokensLabel}</strong>
              </div>
              <div className="shop-checkout__summary-row">
                <span>Type</span>
                <strong>
                  {pkg.recurring ? 'Maandelijks abonnement' : 'Eenmalig'}
                </strong>
              </div>
              <div className="shop-checkout__summary-row">
                <span>Subtotaal (ex BTW)</span>
                <strong>€{pkg.priceEx}</strong>
              </div>
              <div className="shop-checkout__summary-row">
                <span>BTW {pkg.vatRate}%</span>
                <strong>€{pkg.vat}</strong>
              </div>
              <div className="shop-checkout__summary-row shop-checkout__summary-row--total">
                <span>Totaal (incl. BTW)</span>
                <strong>€{pkg.priceIncl}</strong>
              </div>
            </section>

            <section className="shop-checkout__methods">
              <h2 className="shop-checkout__h2">Betaalmethode</h2>
              {['iDEAL', 'Creditcard', 'PayPal'].map((m) => (
                <label key={m} className="shop-checkout__method">
                  <input
                    type="radio"
                    name="method"
                    checked={paymentMethod === m}
                    onChange={() => setPaymentMethod(m)}
                  />
                  <span className="shop-checkout__method-label">
                    <span className="shop-checkout__method-icon">
                      {m === 'iDEAL' ? '🏦' : m === 'Creditcard' ? '💳' : '🅿️'}
                    </span>
                    {m}
                  </span>
                </label>
              ))}
            </section>

            {payError && (
              <div className="shop-checkout__error" role="alert">{payError}</div>
            )}

            <button className="shop-checkout__pay" onClick={handlePay} disabled={paying}>
              {paying ? 'Bezig met betalen…' : `Betaal €${pkg.priceIncl}`}
            </button>

            <p className="shop-checkout__fineprint">
              Door te betalen ga je akkoord met onze algemene voorwaarden.
              {pkg.recurring && ' Het abonnement is maandelijks opzegbaar.'}
            </p>
          </>
        ) : (
          <>
            <section className="shop-checkout__success">
              <div className="shop-checkout__success-icon">✅</div>
              <h2 className="shop-checkout__success-title">Betaling gelukt!</h2>
              <p className="shop-checkout__success-sub">
                {pkg.recurring
                  ? 'Je Unlimited abonnement is actief.'
                  : `${pkg.tokensLabel} tokens zijn aan je account toegevoegd.`}
              </p>
            </section>

            {/* Bug 6: animated email confirmation */}
            <div
              className={
                'shop-checkout__email-status ' +
                (emailStatus === 'sending'
                  ? 'shop-checkout__email-status--sending'
                  : 'shop-checkout__email-status--sent')
              }
            >
              {emailStatus === 'sending' && (
                <>
                  <span className="shop-checkout__email-spinner">📧</span>
                  Bevestiging wordt verstuurd...
                </>
              )}
              {emailStatus === 'sent' && order && (
                <>
                  <span>✅</span>
                  E-mail verstuurd naar <strong>{order.email}</strong>
                </>
              )}
            </div>

            <button
              className="shop-checkout__preview-toggle"
              onClick={() => setShowEmail((v) => !v)}
              aria-expanded={showEmail}
            >
              <span>📨 Bekijk bevestigingsmail</span>
              <span className="shop-checkout__preview-chev">
                {showEmail ? '▲' : '▼'}
              </span>
            </button>

            {showEmail && order && (
              <section className="shop-checkout__email-preview">
                <div className="shop-checkout__email-head">
                  <div><strong>VAN:</strong> {pkg.recurring ? 'abonnementen@walkin.nl' : 'bestellingen@walkin.nl'}</div>
                  <div><strong>AAN:</strong> {order.email}</div>
                  <div><strong>ONDERWERP:</strong> {pkg.recurring
                    ? 'Welkom bij WalkIn Unlimited! 🎉'
                    : `Bevestiging — ${pkg.tokensLabel} Deal Tokens gekocht`}</div>
                </div>
                <div className="shop-checkout__email-body">
                  {pkg.recurring ? (
                    <>
                      <p>Beste {order.shopName},</p>
                      <p>Welkom bij WalkIn Unlimited!</p>
                      <p>Uw abonnement is actief:</p>
                      <ul>
                        <li>Plan: <strong>Unlimited</strong></li>
                        <li>Prijs: €99,00 ex BTW / maand (€119,79 incl. BTW)</li>
                        <li>Startdatum: {order.date}</li>
                        <li>Volgende incasso: {order.date} (+ 1 maand)</li>
                        <li>Opzeggen: stuur mail naar opzeggen@walkin.nl</li>
                      </ul>
                      <p><strong>U kunt nu ONBEPERKT deals aanmaken!</strong></p>
                      <p>Transactienummer: {order.transactionId}</p>
                      <p>De factuur vindt u als bijlage.</p>
                      <p>Met vriendelijke groet,<br />Team WalkIn</p>
                    </>
                  ) : (
                    <>
                      <p>Beste {order.shopName},</p>
                      <p>Uw bestelling is bevestigd!</p>
                      <p><strong>BESTELLING:</strong></p>
                      <ul>
                        <li>{pkg.name} — {pkg.tokensLabel} deal tokens</li>
                        <li>Subtotaal: €{pkg.priceEx} ex BTW</li>
                        <li>BTW 21%: €{pkg.vat}</li>
                        <li>Totaal: €{pkg.priceIncl} incl. BTW</li>
                      </ul>
                      <p>
                        Transactienummer: {order.transactionId}<br />
                        Betaalmethode: {order.paymentMethod}<br />
                        Datum: {order.date}
                      </p>
                      <p><strong>Uw token saldo is bijgewerkt.</strong></p>
                      <p>De factuur vindt u als bijlage.</p>
                      <button className="shop-checkout__email-pdfbtn" onClick={handleDownloadPdf}>
                        ⬇️ Download factuur PDF
                      </button>
                      <p>Met vriendelijke groet,<br />Team WalkIn<br />hello@walkin.nl | walkin.nl</p>
                    </>
                  )}
                </div>
              </section>
            )}

            <div className="shop-checkout__action-row">
              <button
                className="shop-checkout__btn shop-checkout__btn--blue"
                onClick={handleDownloadPdf}
              >
                <span>⬇️</span>
                <span>📄 Factuur downloaden</span>
              </button>
              <button
                className="shop-checkout__btn shop-checkout__btn--gray"
                onClick={handleEmail}
              >
                📧 Factuur per e-mail
              </button>
            </div>

            <button
              className="shop-checkout__preview-toggle"
              onClick={() => setShowPreview((v) => !v)}
              aria-expanded={showPreview}
            >
              <span>Bekijk factuur</span>
              <span className="shop-checkout__preview-chev">
                {showPreview ? '▲' : '▼'}
              </span>
            </button>

            {showPreview && order && (
              <section className="shop-checkout__invoice-preview">
                <div className="shop-checkout__inv-head">
                  <div className="shop-checkout__inv-brand">WALKIN FACTUUR</div>
                  <div className="shop-checkout__inv-num">{order.invoiceNumber}</div>
                  <div className="shop-checkout__inv-date">{order.date}</div>
                </div>
                <div className="shop-checkout__inv-section">
                  <div className="shop-checkout__inv-label">Aan:</div>
                  <div className="shop-checkout__inv-value">{order.shopName}</div>
                  <div className="shop-checkout__inv-muted">{order.email}</div>
                </div>
                <div className="shop-checkout__inv-section">
                  <div className="shop-checkout__inv-line">
                    <span>{pkg.name} tokens</span>
                  </div>
                  <div className="shop-checkout__inv-line">
                    <span>Subtotaal</span>
                    <span>€{pkg.priceEx} ex BTW</span>
                  </div>
                  <div className="shop-checkout__inv-line">
                    <span>BTW {pkg.vatRate}%</span>
                    <span>€{pkg.vat}</span>
                  </div>
                  <div className="shop-checkout__inv-line shop-checkout__inv-line--total">
                    <span>TOTAAL</span>
                    <span>€{pkg.priceIncl}</span>
                  </div>
                </div>
                <div className="shop-checkout__inv-section shop-checkout__inv-paid">
                  <div>✓ BETAALD via {order.paymentMethod}</div>
                  <div className="shop-checkout__inv-muted">
                    Transactie: {order.transactionId}
                  </div>
                </div>
              </section>
            )}

            <button
              className="shop-checkout__pay"
              onClick={() => navigate('/shop/dashboard')}
            >
              Terug naar dashboard
            </button>
          </>
        )}
      </main>

      {toast && <div className="shop-checkout__toast">{toast}</div>}

      <ShopBottomNav />
    </div>
  );
}
